'use client';

import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { supabase, isSupabaseAvailable } from '@/lib/supabaseClient';
import Image from 'next/image';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PublicStoryPreview from '@/components/PublicStoryPreview';

interface FeaturedStory {
  id: string;
  title: string;
  description: string;
  cover_image_url?: string;
  page_count: number;
  tags: string[];
  target_age_min: number;
  target_age_max: number;
}

export default function HomePage() {
  const router = useRouter();
  const [isAnnual, setIsAnnual] = useState(false);
  const [featuredStory, setFeaturedStory] = useState<FeaturedStory | null>(null);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [speechBubble, setSpeechBubble] = useState<{message: string, show: boolean}>({message: '', show: false});
  const [showStoryPreview, setShowStoryPreview] = useState(false);

  const monthlyPrice = 9.99;
  const annualPrice = 99.99;
  const annualSavings = (monthlyPrice * 12) - annualPrice;

  useEffect(() => {
    loadFeaturedStory();
  }, []);

  const loadFeaturedStory = async () => {
    if (!isSupabaseAvailable() || !supabase) {
      setLoadingFeatured(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('featured_story', true)
        .eq('status', 'published')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        console.error('Error loading featured story:', error);
      } else if (data) {
        setFeaturedStory(data);
      }
    } catch (error) {
      console.error('Error loading featured story:', error);
    } finally {
      setLoadingFeatured(false);
    }
  };

  // Sample story data for preview
  const sampleStoryPages = [
    {
      pageNumber: 1,
      text: "Captain Monster Truck woke up feeling excited but a little nervous. Today was his first day at the new school!",
      illustration: "🚛"
    },
    {
      pageNumber: 2,
      text: "He packed his favorite lunch and put on his shiniest bumper. 'You've got this!' said Mom with a big hug.",
      illustration: "🎒"
    },
    {
      pageNumber: 3,
      text: "At school, Captain Monster Truck met Bella Bus and Freddy Fire Truck. They showed him around and became fast friends!",
      illustration: "🏫"
    }
  ];

  const handleOpenPreview = () => {
    setShowStoryPreview(true);
  };

  const handleClosePreview = () => {
    setShowStoryPreview(false);
  };

  const handleGetStarted = () => {
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setShowStoryPreview(false);
  };

  const handleSocialClick = (platform: string) => {
    if (platform === 'instagram') {
      window.open('https://www.instagram.com/monstertruckstories/', '_blank');
    } else if (platform === 'tiktok') {
      window.open('https://www.tiktok.com/@monstertruckstories', '_blank');
    }
  };

  const handleTruckClick = () => {
    const messages = [
      'VROOM VROOM! Ready for some epic adventures? 🚛💨',
      'Hey there, adventure buddy! Let\'s roll into some amazing stories! 📚✨',
      'BEEP BEEP! Captain Monster Truck here! Want to hear about courage? 🦸‍♂️',
      'Rolling in with faith-friendly fun! Got your imagination ready? 🌟',
      'Big wheels, bigger dreams! Let\'s learn something awesome today! 🎆',
      'Honk honk! Ready to join our story family? It\'s gonna be EPIC! 🚛❤️',
      'Engine revving! Story time is the BEST time! Who\'s excited? 📖🎉',
      'Zoom zoom! Every day is a new adventure when we\'re together! 🌈',
      'Monster truck powers: ACTIVATED! Let\'s discover something amazing! ⚡'
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)] || 'VROOM VROOM! Ready for adventure! 🚛';

    // Show speech bubble
    setSpeechBubble({ message: randomMessage, show: true });

    // Hide speech bubble after 4 seconds
    setTimeout(() => {
      setSpeechBubble(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const dismissSpeechBubble = () => {
    setSpeechBubble(prev => ({ ...prev, show: false }));
  };

  return (
    <div className="min-h-screen bg-sky-300 overflow-x-hidden">
      <Header onTruckClick={handleTruckClick} onSocialClick={handleSocialClick} />

      {/* Hero Section */}
      <main>
      <section className="hero relative bg-gradient-to-br from-sky-300 via-sky-200 to-green-200 py-12 overflow-hidden" id="home" aria-labelledby="hero-title">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 text-3xl animate-bounce">⭐</div>
          <div className="absolute top-30 right-15 text-3xl animate-bounce" style={{animationDelay: '1s'}}>💨</div>
          <div className="absolute bottom-30 left-20 text-3xl animate-bounce" style={{animationDelay: '2s'}}>🎯</div>
          <div className="absolute bottom-40 right-10 text-3xl animate-bounce" style={{animationDelay: '3s'}}>⚡</div>
        </div>
        
        <div className="hero-container max-w-7xl mx-auto px-8 grid md:grid-cols-2 gap-12 items-center relative z-10">
          <div className="hero-content text-slate-800">
            <div className="hero-badge bg-white/20 text-white border-white/30 mb-6 text-base font-semibold inline-block px-6 py-3 rounded-full border-2">
              Now Available
            </div>
            <h1 id="hero-title" className="font-bungee text-5xl md:text-6xl mb-6 text-slate-800 drop-shadow-lg leading-tight">
              Big Adventures for Little Learners
            </h1>
            <p className="text-xl mb-8 text-slate-800 font-medium leading-relaxed">
              Stories that shape young minds for a healthy, happy future.
            </p>
            
            <div className="hero-actions flex max-w-lg mb-6">
              <Button
                onClick={() => {
                  const el = document.getElementById('pricing');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="hero-btn bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg px-8 py-4 rounded-xl uppercase transition-all hover:scale-105 w-full"
                aria-label="Get started - scroll to pricing section"
              >
                Get Started
              </Button>
            </div>
          </div>
          
          <div className="hero-visual flex justify-center items-center relative">
            <Image
              src="/assets/Captain Monster Truck 1-Photoroom.png"
              alt="Captain Monster Truck - Friendly cartoon monster truck character for kids"
              width={384}
              height={384}
              priority
              className="hero-captain-truck object-contain animate-truck-drive filter drop-shadow-2xl cursor-pointer hover:scale-105 transition-transform"
              onClick={handleTruckClick}
            />

            {/* Comic Book Speech Bubble */}
            {speechBubble.show && (
              <>
                {/* Invisible overlay to catch clicks anywhere */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={dismissSpeechBubble}
                ></div>

                <div className={`absolute -top-4 -right-8 max-w-xs z-20 transition-all duration-500 transform ${speechBubble.show ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                  <div className="relative bg-white border-4 border-slate-800 rounded-2xl p-4 shadow-xl">
                    {/* Speech bubble tail */}
                    <div className="absolute bottom-0 left-8 w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-t-[20px] border-t-white transform translate-y-full -translate-x-2"></div>
                    <div className="absolute bottom-0 left-8 w-0 h-0 border-l-[18px] border-l-transparent border-r-[18px] border-r-transparent border-t-[23px] border-t-slate-800 transform translate-y-full -translate-x-2 z-10"></div>

                    {/* Comic book styling */}
                    <div className="font-bold text-slate-800 text-sm leading-tight">
                      {speechBubble.message}
                    </div>

                    {/* Comic book sound effects */}
                    <div className="absolute -top-3 -right-3 bg-yellow-400 border-2 border-slate-800 rounded-full px-2 py-1 text-xs font-black text-slate-800 transform rotate-12 animate-pulse">
                      POW!
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="box-section bg-gradient-to-br from-blue-400 to-blue-600 py-16 text-white" id="about" aria-labelledby="about-title">
        <div className="box-container max-w-7xl mx-auto px-8 grid md:grid-cols-2 gap-16 items-center">
          <div className="about-image-container flex justify-center items-center">
            <Image
              src="/assets/about_us-Photoroom.png"
              alt="About Monster Truck Stories - Educational storytelling for toddlers"
              width={320}
              height={320}
              className="about-us-image object-contain filter drop-shadow-xl cursor-pointer hover:scale-105 transition-transform"
              onClick={handleTruckClick}
            />
          </div>
          <div className="about-content">
            <h2 id="about-title" className="font-bungee text-4xl mb-8 text-white drop-shadow-lg">
              WHAT IT IS
            </h2>
            <p className="text-xl leading-relaxed opacity-95">
              Monster Truck Stories is a storytelling web app designed especially for toddlers ages 0–4. Each week, families discover colorful adventures featuring our lovable hero, Captain Monster Truck. Every story is created to spark imagination, teach early life skills, and share genuine Christian values in a safe, fun, and incorporating mom and dad hobbies.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Story Section */}
      <section className="featured-story-section bg-gradient-to-br from-cyan-400 to-blue-500 py-16 text-white relative overflow-hidden">
        <div className="featured-container max-w-7xl mx-auto px-8 text-center">
          <h2 className="featured-title font-bungee text-5xl mb-4 text-white drop-shadow-lg">
            FEATURED STORY
          </h2>
          <p className="featured-subtitle text-xl mb-12 opacity-95">
            Get a sneak peek at one of Captain Monster Truck&rsquo;s exciting adventures!
          </p>
          
          <div className="story-showcase max-w-5xl mx-auto relative">
            <div className="story-preview-card bg-white/95 border-4 border-white/80 shadow-2xl hover:-translate-y-1 transition-transform rounded-3xl p-12 grid md:grid-cols-3 gap-12 items-center">
              <div className="story-preview-image flex flex-col items-center justify-center space-y-4">
                <div className="story-book text-6xl animate-bounce">📚</div>
                <div className="story-truck text-5xl animate-bounce" style={{animationDelay: '0.5s'}}>🚛</div>
              </div>
              <div className="story-preview-content md:col-span-2 text-slate-800">
                {loadingFeatured ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading featured story...</p>
                  </div>
                ) : featuredStory ? (
                  <>
                    <h3 className="font-bungee text-2xl mb-6 text-slate-800">
                      &ldquo;{featuredStory.title}&rdquo;
                    </h3>
                    <p className="story-description text-lg mb-8 text-slate-600 leading-relaxed">
                      {featuredStory.description}
                    </p>
                    
                    <div className="story-features flex flex-wrap gap-4 mb-8">
                      <div className="story-feature flex items-center gap-2 bg-blue-100 px-4 py-3 rounded-2xl border-2 border-blue-300">
                        <span className="feature-icon text-blue-600">📖</span>
                        <span className="font-semibold text-slate-800">{featuredStory.page_count} Pages</span>
                      </div>
                      <div className="story-feature flex items-center gap-2 bg-blue-100 px-4 py-3 rounded-2xl border-2 border-blue-300">
                        <span className="feature-icon text-blue-600">🎵</span>
                        <span className="font-semibold text-slate-800">Voice Narration</span>
                      </div>
                      <div className="story-feature flex items-center gap-2 bg-blue-100 px-4 py-3 rounded-2xl border-2 border-blue-300">
                        <span className="feature-icon text-blue-600">⭐</span>
                        <span className="font-semibold text-slate-800">Ages {featuredStory.target_age_min}-{featuredStory.target_age_max}</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleOpenPreview}
                      className="preview-story-btn bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold text-lg px-8 py-4 rounded-full uppercase shadow-lg hover:-translate-y-1 transition-all"
                    >
                      Preview Story
                    </Button>
                  </>
                ) : (
                  <>
                    <h3 className="font-bungee text-2xl mb-6 text-slate-800">
                      &ldquo;Captain Monster Truck&rsquo;s First Day at School&rdquo;
                    </h3>
                    <p className="story-description text-lg mb-8 text-slate-600 leading-relaxed">
                      Join Captain Monster Truck as he discovers that being nervous about new things is normal, 
                      and that making friends and being kind makes every adventure better! A genuine story about 
                      courage, friendship, and the joy of learning.
                    </p>
                    
                    <div className="story-features flex flex-wrap gap-4 mb-8">
                      <div className="story-feature flex items-center gap-2 bg-blue-100 px-4 py-3 rounded-2xl border-2 border-blue-300">
                        <span className="feature-icon text-blue-600">📖</span>
                        <span className="font-semibold text-slate-800">8 Pages</span>
                      </div>
                      <div className="story-feature flex items-center gap-2 bg-blue-100 px-4 py-3 rounded-2xl border-2 border-blue-300">
                        <span className="feature-icon text-blue-600">🎵</span>
                        <span className="font-semibold text-slate-800">Voice Narration</span>
                      </div>
                      <div className="story-feature flex items-center gap-2 bg-blue-100 px-4 py-3 rounded-2xl border-2 border-blue-300">
                        <span className="feature-icon text-blue-600">⭐</span>
                        <span className="font-semibold text-slate-800">Ages 2-4</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleOpenPreview}
                      className="preview-story-btn bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold text-lg px-8 py-4 rounded-full uppercase shadow-lg hover:-translate-y-1 transition-all"
                    >
                      Preview Story
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="signup-section bg-gradient-to-br from-green-500 to-green-700 py-16 text-white relative" id="features">
        <div className="signup-container max-w-7xl mx-auto px-8 text-center">
          <h2 className="signup-title font-bungee text-4xl mb-12 text-white drop-shadow-lg">
            WHAT&apos;S INCLUDED
          </h2>
          
          <div className="features-grid grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div className="feature-card bg-white/10 border-2 border-white/20 hover:-translate-y-2 hover:bg-white/15 hover:border-white/30 transition-all min-h-[450px] flex flex-col justify-between p-8 rounded-2xl">
              <div className="feature-image w-full h-48 mb-6 rounded-2xl overflow-hidden shadow-lg">
                <Image src="/assets/story_library.jpeg" alt="Story Library - Browse toddler-friendly stories" width={192} height={192} className="feature-img w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
              <h3 className="text-2xl mb-4 text-yellow-300 font-bold">
                Story Library
              </h3>
              <p className="text-white/90 leading-relaxed flex-grow">
                Browse a growing collection of toddler-friendly stories, each displayed in a simple card layout with fun thumbnails and easy navigation.
              </p>
            </div>

            <div className="feature-card bg-white/10 border-2 border-white/20 hover:-translate-y-2 hover:bg-white/15 hover:border-white/30 transition-all min-h-[450px] flex flex-col justify-between p-8 rounded-2xl">
              <div className="feature-image w-full h-48 mb-6 rounded-2xl overflow-hidden shadow-lg">
                <Image src="/assets/interactive_reader.jpeg" alt="Interactive Reader - Page-by-page story experience" width={192} height={192} className="feature-img w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
              <h3 className="text-2xl mb-4 text-yellow-300 font-bold">
                Interactive Reader
              </h3>
              <p className="text-white/90 leading-relaxed flex-grow">
                Experience stories page by page with text on the left and colorful illustrations on the right. Simple controls make it easy for little ones to follow along.
              </p>
            </div>

            <div className="feature-card bg-white/10 border-2 border-white/20 hover:-translate-y-2 hover:bg-white/15 hover:border-white/30 transition-all min-h-[450px] flex flex-col justify-between p-8 rounded-2xl">
              <div className="feature-image w-full h-48 mb-6 rounded-2xl overflow-hidden shadow-lg">
                <Image src="/assets/voice_narration.jpeg" alt="Voice Narration - Listen to stories read aloud" width={192} height={192} className="feature-img w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
              <h3 className="text-2xl mb-4 text-yellow-300 font-bold">
                Voice Narration
              </h3>
              <p className="text-white/90 leading-relaxed flex-grow">
                Tap the speaker icon to listen as the story is read aloud — perfect for bedtime or screen-free moments.
              </p>
            </div>

            <div className="feature-card bg-white/10 border-2 border-white/20 hover:-translate-y-2 hover:bg-white/15 hover:border-white/30 transition-all min-h-[450px] flex flex-col justify-between p-8 rounded-2xl">
              <div className="feature-image w-full h-48 mb-6 rounded-2xl overflow-hidden shadow-lg">
                <Image src="/assets/parent_peace_of_mind.jpeg" alt="Parent Peace of Mind - Safe, ad-free content for kids" width={192} height={192} className="feature-img w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
              <h3 className="text-2xl mb-4 text-yellow-300 font-bold">
                Parent Peace of Mind
              </h3>
              <p className="text-white/90 leading-relaxed flex-grow">
                Ad-free, curated, and designed for children ages 0–4. You can relax knowing every story is safe, wholesome, and engaging.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mascot-corner absolute bottom-0 right-5 w-20 h-20 animate-bounce">
          <Image
            src="/assets/Captain Monster Truck 1-Photoroom.png"
            alt="Captain Monster Truck mascot"
            width={80}
            height={80}
            className="mascot-image w-full h-full object-contain"
          />
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works bg-gradient-to-br from-slate-800 to-slate-600 py-16 text-white" id="how">
        <div className="how-container max-w-7xl mx-auto px-8 text-center">
          <h2 className="how-title font-bungee text-5xl mb-4 text-white drop-shadow-lg">
            WHY PARENTS WILL LOVE IT
          </h2>
          
          <div className="benefits-list max-w-4xl mx-auto mt-12">
            <div className="space-y-6">
              {[
                'Builds early life skills and healthy habits through simple lessons.',
                'Encourages imagination and character development.',
                'genuine, faith-friendly stories that teach kindness, patience, and courage.',
                'A trusted, ad-free space for your little one.',
                'Affordable subscription with monthly or annual plans.'
              ].map((benefit, index) => (
                <div key={index} className="benefit-item flex items-start p-4 bg-white/10 rounded-2xl border-2 border-white/20">
                  <span className="bullet text-yellow-300 text-2xl mr-4 mt-1">•</span>
                  <p className="text-lg leading-relaxed opacity-95 text-left">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-cta bg-gradient-to-br from-sky-300 to-sky-200 py-16 text-center relative" id="pricing">
        <div className="final-container max-w-5xl mx-auto px-8">
          <h2 className="final-title font-bungee text-5xl mb-4 text-slate-800 drop-shadow-lg">
            Simple, Family-Friendly Pricing
          </h2>
          <p className="final-subtitle text-xl mb-8 text-slate-800">
            Unlimited access to all stories. Cancel anytime.
          </p>
          
          {/* Pricing Toggle */}
          <div className="pricing-toggle mb-8">
            <div className="inline-flex bg-white/80 rounded-full p-1 shadow-lg">
              <button 
                onClick={() => setIsAnnual(false)}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${
                  !isAnnual 
                    ? 'bg-yellow-400 text-slate-800' 
                    : 'text-slate-800 hover:bg-white/50'
                }`}
              >
                Monthly
              </button>
              <button 
                onClick={() => setIsAnnual(true)}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${
                  isAnnual 
                    ? 'bg-yellow-400 text-slate-800' 
                    : 'text-slate-800 hover:bg-white/50'
                }`}
              >
                Annual
              </button>
            </div>
          </div>
          
          {/* Pricing Card */}
          <div className="mx-auto max-w-md bg-white rounded-3xl p-8 shadow-xl relative">
            {/* Gradient Border on Top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-yellow-400 rounded-t-3xl"></div>
            
            <div className="text-2xl font-bold text-slate-800 mb-2">Captain Monster Truck</div>
            
            {/* Price Display */}
            {!isAnnual ? (
              <>
                <div className="text-4xl font-bold text-slate-800 mb-1">${monthlyPrice}</div>
                <div className="text-slate-600 mb-6">/month</div>
              </>
            ) : (
              <>
                <div className="text-4xl font-bold text-slate-800 mb-1">${annualPrice}</div>
                <div className="text-slate-600 mb-2">/year</div>
                <div className="text-green-600 font-semibold mb-6">Save ${annualSavings.toFixed(2)} • 2 months free!</div>
              </>
            )}
            
            <ul className="text-left text-slate-800 space-y-3 mb-8">
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                Unlimited access to all stories
              </li>
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                High-quality audio narration
              </li>
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                New stories added monthly
              </li>
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                Ad-free experience
              </li>
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                Age-appropriate content curation
              </li>
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                Parent discussion guides
              </li>
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                Cancel anytime
              </li>
              <li className="flex items-center gap-3">
                <span className="text-slate-800">✓</span>
                Works on all devices
              </li>
            </ul>
            
            <Button 
              onClick={() => router.push('/login?redirect=onboarding&action=trial')} 
              className="final-btn bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-8 py-4 rounded-xl uppercase transition-all hover:scale-105 w-full mb-4"
            >
              Start 7-day free trial
            </Button>
            
            <div className="text-sm text-slate-500">
              Cancel anytime • Immediate access
            </div>
          </div>
          
          {/* Bottom Trust Indicators */}
          <div className="mt-12 text-slate-700">
            <p className="text-lg mb-6">Trusted by families worldwide</p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <span>30-Day Money Back</span>
              <span className="w-px h-4 bg-slate-400"></span>
              <span>Secure Payments</span>
              <span className="w-px h-4 bg-slate-400"></span>
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <Footer onTruckClick={handleTruckClick} onSocialClick={handleSocialClick} />

      {/* Public Story Preview Modal */}
      <PublicStoryPreview
        isOpen={showStoryPreview}
        onClose={handleClosePreview}
        storyTitle={featuredStory?.title || "Captain Monster Truck's First Day at School"}
        storyDescription={featuredStory?.description || "Join Captain Monster Truck as he discovers that being nervous about new things is normal, and that making friends and being kind makes every adventure better!"}
        previewPages={sampleStoryPages}
        totalPages={featuredStory?.page_count || 8}
        onGetStarted={handleGetStarted}
      />
    </div>
  );
}
